import ReactEcharts from 'echarts-for-react';
import { getWithToken } from "../fetchWithToken";
import { useEffect, useState, useRef } from "react";
import { Card, CardContent, Box, Tab, Tabs, List, ListItem, Typography } from "@mui/material";
import { PieChart, AccountBalanceWallet } from "@mui/icons-material";

// 扁平化获取所有叶子节点的数据（用于排行榜）
const flattenLeafData = (items, type = 'expense') => {
    const result = [];

    const flatten = (item, parentName = '') => {
        const isExpense = item.amount < 0;
        const isIncome = item.amount > 0;

        if ((type === 'expense' && isExpense) || (type === 'income' && isIncome)) {
            const fullName = parentName ? `${parentName} > ${item.category_name}` : item.category_name;

            if (item.children && item.children.length > 0) {
                item.children.forEach(child => flatten(child, fullName));
            } else {
                result.push({
                    category_id: item.category_id,
                    name: fullName,
                    value: Math.abs(item.amount),
                    original_name: item.category_name,
                    count: item.count || 0
                });
            }
        }
    };

    items.forEach(item => flatten(item));
    return result.sort((a, b) => b.value - a.value);
};

// 构建旭日图层级数据，同时记录父级总额用于计算相对百分比
const buildSunburstData = (items, type = 'expense') => {
    const result = [];

    const processItem = (item) => {
        const isExpense = item.amount < 0;
        const isIncome = item.amount > 0;

        if ((type === 'expense' && isExpense) || (type === 'income' && isIncome)) {
            const processedItem = {
                name: item.category_name,
                value: Math.abs(item.amount),
                category_id: item.category_id,
                itemStyle: {
                    borderWidth: 3,
                    borderColor: '#fff'
                }
            };

            if (item.children && item.children.length > 0) {
                const children = [];
                item.children.forEach(child => {
                    const childData = processItem(child);
                    if (childData) {
                        children.push(childData);
                    }
                });
                if (children.length > 0) {
                    processedItem.children = children;
                    // 存储子节点总额，用于计算相对百分比
                    processedItem.childrenTotal = children.reduce((sum, c) => sum + c.value, 0);
                }
            }

            return processedItem;
        }
        return null;
    };

    items.forEach(item => {
        const processed = processItem(item);
        if (processed && processed.value > 0) {
            result.push(processed);
        }
    });

    return result;
};

// 旭日图颜色方案
// 一、二级类别
const LEVEL1_2_COLORS = [
    '#FF6B6B', // 红色
    '#FF9F43', // 橙色
    '#FFD93D', // 黄色
    '#6BCF7F', // 绿色
    '#4ECDC4', // 青色
    '#45B7D1', // 蓝色
    '#A29BFE', // 紫色
    '#FD79A8', // 粉色
    '#F8B195', // 桃色
    '#C44569'  // 深红
];

// 三级及以下类别：基于二级颜色的深浅变化
const LEVEL3_COLORS = [
    ['#E55555', '#FF6B6B', '#FF8787', '#FFB4B4', '#FFD1D1'], // 红色系
    ['#E88E30', '#FF9F43', '#FFB970', '#FFD39A', '#FFE5C0'], // 橙色系
    ['#E6CF30', '#FFD93D', '#FFE570', '#FFF0A0', '#FFFAC0'], // 黄色系
    ['#5AB86C', '#6BCF7F', '#8FD99E', '#B3E9BD', '#D7F5DC'], // 绿色系
    ['#3DBDB5', '#4ECDC4', '#6EE7E0', '#9EFFF8', '#D0FFF8'], // 青色系
    ['#3AA5C1', '#45B7D1', '#6BC9E1', '#9FE3F5', '#D0F0FA'], // 蓝色系
    ['#8C85DD', '#A29BFE', '#BDB0FF', '#D8D0FF', '#F0EBFF'], // 紫色系
    ['#E05A85', '#FD79A8', '#FE9FC0', '#FFC5D8', '#FFE5F0'], // 粉色系
    ['#E89E7F', '#F8B195', '#FFC4AD', '#FFD9CA', '#FFF0E0'], // 桃色系
    ['#A03A58', '#C44569', '#D8708F', '#EC9CB5', '#F5C8D5']  // 深红系
];

export const CategorySunBurst = ({ year, month, dataUrl, title }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tabType, setTabType] = useState('expense');
    const [hoverData, setHoverData] = useState(null);
    const [loadingTop, setLoadingTop] = useState(false);
    const echartsRef = useRef(null);
    const [categoryColorMap, setCategoryColorMap] = useState({});

    // 构建API请求参数
    const getApiParams = () => {
        const params = { year };
        if (month !== undefined) {
            params.month = month;
        }
        return params;
    };

    useEffect(() => {
        setLoading(true);
        getWithToken(dataUrl, getApiParams())
            .then((result) => {
                setData(result || []);
                // 为每个类别预计算颜色
                const colorMap = {};
                let color12 = 0, color3 = 0;

                const assignColors = (items, level = 1, parentColorIndex = 0) => {
                    items.forEach(item => {
                        let colorIndex;
                        const availableColors = level <= 2 ? LEVEL1_2_COLORS :
                                               LEVEL3_COLORS[parentColorIndex] || LEVEL3_COLORS[0];

                        if (level <= 2) {
                            colorIndex = color12 % availableColors.length;
                            colorMap[item.category_id] = availableColors[colorIndex];
                            color12++;
                            parentColorIndex = colorIndex;
                        } else {
                            const colors = LEVEL3_COLORS[parentColorIndex] || LEVEL3_COLORS[0];
                            colorIndex = color3 % colors.length;
                            colorMap[item.category_id] = colors[colorIndex];
                            color3++;
                        }

                        if (item.children) {
                            assignColors(item.children, level + 1, parentColorIndex);
                        }
                    });
                };
                assignColors(result);
                setCategoryColorMap(colorMap);
            })
            .catch(err => {
                console.error(`获取${title}数据失败:`, err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [year, month, dataUrl, title]);

    // 获取类别top5条目
    const fetchTopEntries = async (categoryId) => {
        setLoadingTop(true);
        try {
            const params = {
                category_id: categoryId,
                year: year
            };
            if (month !== undefined) {
                params.month = month;
            }
            const result = await getWithToken('/api/statistic/category_top_entries/', params);
            setHoverData(result);
        } catch (err) {
            console.error('获取top条目失败:', err);
        } finally {
            setLoadingTop(false);
        }
    };

    const getSunburstOption = (type = 'expense') => {
        const sunburstData = buildSunburstData(data, type);
        const leafData = flattenLeafData(data, type);
        const totalAmount = sunburstData.reduce((sum, item) => sum + item.value, 0);

        if (sunburstData.length === 0) return null;

        // 为每个节点分配颜色
        const assignNodeColors = (nodes) => {
            return nodes.map(node => ({
                ...node,
                itemStyle: {
                    ...node.itemStyle,
                    color: categoryColorMap[node.category_id] || '#ccc'
                },
                children: node.children ? assignNodeColors(node.children) : undefined
            }));
        };

        const coloredData = assignNodeColors(sunburstData);

        return {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'item',
                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                borderColor: '#e5e7eb',
                borderWidth: 1,
                textStyle: { color: '#1f2937' },
                extraCssText: 'z-index: 100; box-shadow: 0 4px 12px rgba(0,0,0,0.15);',
                formatter: (params) => {
                    const percent = ((params.value / totalAmount) * 100).toFixed(1);
                    let content = `
                        <div style="font-weight: 600; margin-bottom: 8px; color: #1f2937; font-size: 14px;">${params.name}</div>
                        <div style="color: ${type === 'expense' ? '#10b981' : '#ef4444'}; margin: 4px 0; font-size: 13px;">
                            金额: <strong>¥${params.value.toLocaleString()}</strong>
                        </div>
                        <div style="color: #6b7280; font-size: 12px;">
                            占总金额比例: <strong>${percent}%</strong>
                        </div>
                    `;
                    if (loadingTop) {
                        content += `<div style="color: #9ca3af; font-size: 11px; margin-top: 8px;">加载中...</div>`;
                    } else if (hoverData && hoverData.length > 0) {
                        content += `<div style="margin-top: 10px; padding-top: 8px; border-top: 1px solid #e5e7eb;">
                            <div style="font-size: 11px; color: #6b7280; margin-bottom: 4px;">Top 5 条目:</div>`;
                        hoverData.slice(0, 5).forEach((entry, i) => {
                            const displayContent = entry.content ? (entry.content.length > 20 ? entry.content.substring(0, 20) + '...' : entry.content) : '';
                            content += `<div style="font-size: 11px; color: #374151; margin: 4px 0;">
                                ${i + 1}. ${entry.title}${displayContent ? ` <span style="color: #9ca3af;">- ${displayContent}</span>` : ''} <span style="color: ${type === 'expense' ? '#10b981' : '#ef4444'};">¥${entry.abs_amount.toLocaleString()}</span>
                            </div>`;
                        });
                        content += `</div>`;
                    } else if (hoverData && hoverData.length === 0) {
                        content += `<div style="color: #9ca3af; font-size: 11px; margin-top: 8px;">暂无条目</div>`;
                    }
                    return content;
                }
            },
            series: {
                type: 'sunburst',
                data: coloredData,
                radius: [0, '100%'],
                center: ['50%', '50%'],
                sort: (a, b) => b.value - a.value,
                emphasis: {
                    focus: 'ancestor'
                },
                label: {
                    show: true
                },
                levels: [
                    {
                        r0: '0%',
                        r: '25%',
                        label: {
                            rotate: 0,
                            align: 'center',
                            verticalAlign: 'middle',
                            formatter: (params) => {
                                const percent = ((params.value / totalAmount) * 100);
                                if (percent < 3) return '';
                                return `{title|${params.name}}\n{percent|${percent.toFixed(1)}%}\n{amount|¥${(params.value / 1000).toFixed(1)}k}`;
                            },
                            rich: {
                                title: {
                                    fontSize: 18,
                                    fontWeight: 'bold',
                                    color: '#1f2937',
                                    lineHeight: 24
                                },
                                percent: {
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: '#374151',
                                    lineHeight: 20
                                },
                                amount: {
                                    fontSize: 12,
                                    fontWeight: 500,
                                    color: type === 'expense' ? '#059669' : '#dc2626',
                                    lineHeight: 18
                                }
                            }
                        },
                        itemStyle: {
                            borderWidth: 3,
                            borderColor: '#fff'
                        }
                    },
                    {
                        r0: '25%',
                        r: '55%',
                        label: {
                            rotate: 0,
                            align: 'center',
                            verticalAlign: 'middle',
                            formatter: (params) => {
                                let parentPercent = 0;
                                const treeInfo = params.treePathInfo;
                                if (treeInfo && treeInfo.length >= 2) {
                                    const parentValue = treeInfo[treeInfo.length - 2].value;
                                    parentPercent = parentValue > 0 ? ((params.value / parentValue) * 100) : 0;
                                }
                                if (parentPercent < 1) return '';
                                return `{title|${params.name}}\n{percent|${parentPercent.toFixed(0)}%}`;
                            },
                            rich: {
                                title: {
                                    fontSize: 15,
                                    fontWeight: 'bold',
                                    color: '#1f2937',
                                    lineHeight: 22
                                },
                                percent: {
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: '#4b5563',
                                    lineHeight: 18
                                }
                            }
                        },
                        itemStyle: {
                            borderWidth: 2.5,
                            borderColor: '#fff'
                        }
                    },
                    {
                        r0: '55%',
                        r: '85%',
                        label: {
                            rotate: 0,
                            align: 'center',
                            verticalAlign: 'middle',
                            formatter: (params) => {
                                let parentPercent = 0;
                                const treeInfo = params.treePathInfo;
                                if (treeInfo && treeInfo.length >= 2) {
                                    const parentValue = treeInfo[treeInfo.length - 2].value;
                                    parentPercent = parentValue > 0 ? ((params.value / parentValue) * 100) : 0;
                                }
                                return `{name|${params.name}}\n{percent|${parentPercent.toFixed(0)}%}`;
                            },
                            rich: {
                                name: {
                                    fontSize: 14,
                                    fontWeight: 500,
                                    color: '#1f2937'
                                },
                                percent: {
                                    fontSize: 13,
                                    fontWeight: 500,
                                    color: '#6b7280'
                                }
                            }
                        },
                        itemStyle: {
                            borderWidth: 2,
                            borderColor: '#fff'
                        }
                    },
                    {
                        r0: '85%',
                        r: '95%',
                        label: {
                            rotate: 0,
                            align: 'center',
                            verticalAlign: 'middle',
                            formatter: (params) => {
                                let parentPercent = 0;
                                const treeInfo = params.treePathInfo;
                                if (treeInfo && treeInfo.length >= 2) {
                                    const parentValue = treeInfo[treeInfo.length - 2].value;
                                    parentPercent = parentValue > 0 ? ((params.value / parentValue) * 100) : 0;
                                }
                                return parentPercent > 0 ? `{percent|${parentPercent.toFixed(0)}%}` : '';
                            },
                            rich: {
                                percent: {
                                    fontSize: 10,
                                    fontWeight: 600,
                                    color: '#6b7280'
                                }
                            }
                        },
                        itemStyle: {
                            borderWidth: 1.5,
                            borderColor: '#fff'
                        }
                    }
                ]
            }
        };
    };

    const leafData = flattenLeafData(data, tabType);
    const totalAmount = leafData.reduce((sum, item) => sum + item.value, 0);

    const expenseOption = getSunburstOption('expense');
    const incomeOption = getSunburstOption('income');

    const onChartReady = (echarts) => {
        echartsRef.current = echarts;
        echarts.on('mouseover', (params) => {
            if (params.data && params.data.category_id) {
                fetchTopEntries(params.data.category_id);
            }
        });
    };

    if (loading) {
        return (
            <Card sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <CardContent sx={{ height: 450, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">加载中...</Typography>
                </CardContent>
            </Card>
        );
    }

    if (!expenseOption && !incomeOption) {
        return (
            <Card sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <CardContent sx={{ height: 450, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">暂无数据</Typography>
                </CardContent>
            </Card>
        );
    }

    if (!incomeOption) setTabType('expense');
    if (!expenseOption) setTabType('income');

    return (
        <Card sx={{
            borderRadius: 3,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            transition: 'all 0.3s ease',
            overflow: 'visible',
            '&:hover': {
                boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
            }
        }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1 }}>
                <Tabs
                    value={tabType}
                    onChange={(_, v) => setTabType(v)}
                    sx={{
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontSize: 14,
                            fontWeight: 500,
                            minWidth: 75
                        }
                    }}
                >
                    {expenseOption && (
                        <Tab
                            value="expense"
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <PieChart sx={{ mr: 0.5, fontSize: 18 }} />
                                    支出
                                </Box>
                            }
                        />
                    )}
                    {incomeOption && (
                        <Tab
                            value="income"
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <AccountBalanceWallet sx={{ mr: 0.5, fontSize: 18 }} />
                                    收入
                                </Box>
                            }
                        />
                    )}
                </Tabs>
            </Box>

            <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box sx={{ flex: '0 0 60%' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 2, textAlign: 'center' }}>
                            {tabType === 'expense' ? '支出类别分布' : '收入类别分布'}
                        </Typography>
                        {(tabType === 'expense' && expenseOption) && (
                            <ReactEcharts
                                option={expenseOption}
                                style={{ height: '600px' }}
                                onChartReady={onChartReady}
                            />
                        )}
                        {(tabType === 'income' && incomeOption) && (
                            <ReactEcharts
                                option={incomeOption}
                                style={{ height: '600px' }}
                                onChartReady={onChartReady}
                            />
                        )}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 260 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1f2937'}}>
                            {tabType === 'expense' ? '支出' : '收入'}排行榜 (Top 5)
                        </Typography>
                        <List dense sx={{ py: 0 }}>
                            {leafData.slice(0, 5).map((item, index) => (
                                <ListItem
                                    key={item.category_id}
                                    sx={{
                                        px: 0,
                                        py: 1.2,
                                        borderBottom: index < 4 ? '1px solid #f3f4f6' : 'none'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            bgcolor: index < 3 ? (tabType === 'expense' ? '#ecfdf5' : '#fef2f2') : 'grey.100',
                                            color: index < 3 ? (tabType === 'expense' ? '#10b981' : '#ef4444') : 'text.secondary',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 13,
                                            fontWeight: 700,
                                            mr: 1.5,
                                            flexShrink: 0
                                        }}
                                    >
                                        {index + 1}
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: 14,
                                                fontWeight: 500,
                                                color: '#374151',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}
                                            title={item.name}
                                        >
                                            {item.name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: 12 }}>
                                            {item.count}笔
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: tabType === 'expense' ? '#10b981' : '#ef4444', fontSize: 15 }}>
                                            ¥{item.value.toLocaleString()}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: 12 }}>
                                            {((item.value / totalAmount) * 100).toFixed(1)}%
                                        </Typography>
                                    </Box>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};
